// Hero canvas — de route over de kaart.
// Een licht gekanteld grondvlak met vlakke topografische contour-pieken (de
// keuzes, aanbiedingen en systemen waar een bedrijf mee te maken krijgt) — een
// kaart, geen sterrenhemel. Eén zelfverzekerde amber route — Aligneo — klimt er
// met intentie overheen, begint én eindigt buiten beeld (eindeloze loop), en
// licht de pieken op die ze passeert ("werkprocessen in kaart, automatiseren wat
// verschil maakt"). Een amber baken glijdt omhoog over de route.
// Geen terrein-hoogte, geen kompas. Eén amber/cream/ink-palet. Geen dependency.

const AMBER = [221, 154, 94];      // --amber
const AMBER_HOT = [233, 170, 112]; // --amber-hot
const CREAM = [214, 198, 164];     // --cream-dim (dark scope)

const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

function catmull(pts, t) {
    const tc = Math.max(0, Math.min(t, 0.9999999));
    const seg = (pts.length - 1) * tc;
    const i = Math.min(Math.floor(seg), pts.length - 2);
    const u = seg - i;
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    const u2 = u * u;
    const u3 = u2 * u;
    return {
        u: 0.5 * (2 * p1.u + (-p0.u + p2.u) * u + (2 * p0.u - 5 * p1.u + 4 * p2.u - p3.u) * u2 + (-p0.u + 3 * p1.u - 3 * p2.u + p3.u) * u3),
        v: 0.5 * (2 * p1.v + (-p0.v + p2.v) * u + (2 * p0.v - 5 * p1.v + 4 * p2.v - p3.v) * u2 + (-p0.v + 3 * p1.v - 3 * p2.v + p3.v) * u3),
    };
}

// Plane-coords: u = breedte, v = diepte (0 ver/boven, 1 nabij/onder). Begin- én
// eindpunt buiten beeld. Gladde, monotoon klimmende sweep (geen geslinger).
// Diagonale klim linksonder → rechtsboven, met een lichte weving om de
// contour-pieken heen (geen switchback-overdaad). v daalt monotoon (nooit terug
// naar beneden); u stijgt netto met één zachte slinger. Begin/eind buiten beeld.
const PATH_LAND = [
    { u: -0.16, v: 1.16 },
    { u: 0.16, v: 0.93 },
    { u: 0.40, v: 0.76 },
    { u: 0.34, v: 0.58 },
    { u: 0.60, v: 0.44 },
    { u: 0.70, v: 0.26 },
    { u: 0.92, v: 0.12 },
    { u: 1.20, v: -0.06 },
];
const PATH_PORT = [
    { u: 0.05, v: 1.18 },
    { u: 0.26, v: 0.92 },
    { u: 0.46, v: 0.72 },
    { u: 0.40, v: 0.54 },
    { u: 0.62, v: 0.40 },
    { u: 0.72, v: 0.20 },
    { u: 0.92, v: -0.16 },
];

// Topografische pieken op het vlak (concentrische contour-ringen) — een
// klimmende rug rechts; de route skirt hun linker/onder-flank.
const PEAKS_LAND = [
    { u: 0.44, v: 0.78, rings: 6, max: 0.16 },
    { u: 0.72, v: 0.52, rings: 5, max: 0.13 },
    { u: 0.94, v: 0.30, rings: 4, max: 0.09 },
];
const PEAKS_PORT = [
    { u: 0.60, v: 0.74, rings: 5, max: 0.16 },
    { u: 0.42, v: 0.42, rings: 4, max: 0.12 },
];

function makeGrain() {
    const g = document.createElement("canvas");
    g.width = 120;
    g.height = 120;
    const gx = g.getContext("2d");
    const img = gx.createImageData(120, 120);
    for (let i = 0; i < img.data.length; i += 4) {
        const n = (Math.random() * 255) | 0;
        img.data[i] = img.data[i + 1] = img.data[i + 2] = n;
        img.data[i + 3] = 255;
    }
    gx.putImageData(img, 0, 0);
    return g;
}

export function initHeroCanvas() {
    const cnv = document.querySelector("[data-hero-canvas]");
    if (!cnv) return;
    const ctx = cnv.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0;
    let H = 0;
    let raf = null;
    let running = false;
    let isPortrait = false;
    let isMobile = false;
    const t0 = performance.now();

    let mx = 0.5;
    let smx = 0.5;
    const PARALLAX = 18;

    // Kantel-projectie (vlak grondvlak, lichte foreshortening).
    const FAR_Y = 0.15;
    const NEAR_Y = 1.04;
    const PERSP = 0.82;
    const SPREAD = 1.18;
    const FAR_S = 0.58;
    const NEAR_S = 1.16;
    const TILT_SQUASH = 0.46; // contour-ringen platter → kaart onder een hoek

    const PATH_N = 160;
    let pathPlane = [];
    let peaks = [];

    const grain = makeGrain();
    let grainPattern = null;

    // v binnen [0..1] = vlak; daarbuiten lineair → lijn loopt buiten beeld.
    const project = (u, v, cam) => {
        let ve;
        if (v < 0) ve = v;
        else if (v > 1) ve = 1 + (v - 1);
        else ve = Math.pow(v, PERSP);
        const s = FAR_S + (NEAR_S - FAR_S) * Math.max(0.05, ve);
        const y = (FAR_Y + (NEAR_Y - FAR_Y) * ve) * H;
        const x = W * 0.5 + (u - 0.5) * W * SPREAD * s + cam * s;
        return { x, y, s };
    };

    // Pad samplen + per piek het dichtstbijzijnde route-punt (voor de glow-pass).
    const buildRoute = () => {
        const ctrl = isPortrait ? PATH_PORT : PATH_LAND;
        pathPlane = [];
        for (let i = 0; i <= PATH_N; i++) pathPlane.push(catmull(ctrl, i / PATH_N));

        const src = isPortrait ? PEAKS_PORT : PEAKS_LAND;
        const scr = pathPlane.map((p) => project(p.u, p.v, 0));
        peaks = src.map((pk) => {
            const ps = project(pk.u, pk.v, 0);
            let best = Infinity;
            let bestI = 0;
            for (let i = 0; i <= PATH_N; i++) {
                const dx = scr[i].x - ps.x;
                const dy = scr[i].y - ps.y;
                const d = dx * dx + dy * dy;
                if (d < best) {
                    best = d;
                    bestI = i;
                }
            }
            return { ...pk, tPeak: bestI / PATH_N };
        });
    };

    const resize = () => {
        const r = cnv.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        W = r.width;
        H = r.height;
        cnv.width = Math.max(1, W * dpr);
        cnv.height = Math.max(1, H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        isPortrait = W / H < 1.0;
        isMobile = W < 760;
        grainPattern = ctx.createPattern(grain, "repeat");
        buildRoute();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(cnv);

    const beaconPhase = (t) => (reduced ? 0.5 : (t * 0.05) % 1);

    const passGlow = (tMark, bt) => {
        if (reduced) return tMark <= bt ? 0.7 : 0.12;
        let phase = bt - tMark;
        if (phase < 0) phase += 1;
        return clamp01(Math.exp(-phase * 4.0) + 0.1 * Math.exp(-(1 - phase) * 9));
    };

    // Subtiele grond-lift bij de nabij-rand: een vloer, geen leegte.
    const drawGround = () => {
        const g = ctx.createLinearGradient(0, H * 0.5, 0, H);
        g.addColorStop(0, rgba(CREAM, 0));
        g.addColorStop(1, rgba(CREAM, 0.024));
        ctx.fillStyle = g;
        ctx.fillRect(0, H * 0.5, W, H * 0.5);
    };

    // Survey-grid (graticule): een fijn perspectief-raster dat het vlak als
    // gekarteerd oppervlak leest — een vloer, geen sterrenweb. Heel laag alpha,
    // vervaagt naar de verte zodat het de kop niet vertroebelt.
    const drawGrid = (cam) => {
        const uN = isPortrait ? 6 : 9;
        for (let i = 0; i <= uN; i++) {
            const u = -0.15 + 1.3 * (i / uN);
            ctx.strokeStyle = rgba(CREAM, 0.07);
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            for (let j = 0; j <= 20; j++) {
                const p = project(u, j / 20, cam);
                if (j === 0) ctx.moveTo(p.x, p.y);
                else ctx.lineTo(p.x, p.y);
            }
            ctx.stroke();
        }
        const vN = isPortrait ? 8 : 7;
        for (let i = 0; i <= vN; i++) {
            const v = i / vN;
            const a = project(-0.25, v, cam);
            const b = project(1.25, v, cam);
            ctx.strokeStyle = rgba(CREAM, 0.035 + 0.06 * v);
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
        }
    };

    // Topografische pieken: concentrische contour-ringen, platgedrukt door de tilt.
    const drawPeaks = (t, cam) => {
        const bt = beaconPhase(t);
        for (const pk of peaks) {
            const c = project(pk.u, pk.v, cam);
            const glow = passGlow(pk.tPeak, bt);
            const breathe = 1 + Math.sin(t * 0.5 + pk.u * 6) * 0.05;
            for (let r = 0; r < pk.rings; r++) {
                const norm = (r + 1) / pk.rings;
                const rx = norm * pk.max * H * c.s * breathe;
                const ry = rx * TILT_SQUASH;
                const accent = r % 3 === 0;
                const base = accent ? 0.11 : 0.06;
                ctx.strokeStyle = accent
                    ? rgba(AMBER, base + 0.5 * glow * (1 - norm * 0.5))
                    : rgba(CREAM, base + 0.18 * glow * (1 - norm * 0.5));
                ctx.lineWidth = accent ? 0.9 : 0.7;
                ctx.beginPath();
                ctx.ellipse(c.x, c.y, rx, ry, 0, 0, Math.PI * 2);
                ctx.stroke();
            }
            // Centrum-kruisje (kaartmarkering), licht op bij passage.
            const cross = 4 * c.s;
            ctx.strokeStyle = rgba(AMBER, 0.22 + 0.6 * glow);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(c.x - cross, c.y);
            ctx.lineTo(c.x + cross, c.y);
            ctx.moveTo(c.x, c.y - cross * TILT_SQUASH);
            ctx.lineTo(c.x, c.y + cross * TILT_SQUASH);
            ctx.stroke();
            if (glow > 0.05) {
                const R = (12 + glow * 10) * c.s;
                const halo = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, R);
                halo.addColorStop(0, rgba(AMBER_HOT, 0.22 * glow));
                halo.addColorStop(1, rgba(AMBER_HOT, 0));
                ctx.fillStyle = halo;
                ctx.beginPath();
                ctx.arc(c.x, c.y, R, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    };

    // De route — bold, zelfverzekerd, het brandpunt van de hero.
    const drawGuide = (cam) => {
        const trace = () => {
            const p0 = project(pathPlane[0].u, pathPlane[0].v, cam);
            ctx.beginPath();
            ctx.moveTo(p0.x, p0.y);
            for (let i = 1; i <= PATH_N; i++) {
                const p = project(pathPlane[i].u, pathPlane[i].v, cam);
                ctx.lineTo(p.x, p.y);
            }
        };
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        trace();
        ctx.strokeStyle = rgba(AMBER, 0.13);
        ctx.lineWidth = 8;
        ctx.stroke();
        trace();
        ctx.strokeStyle = rgba(AMBER, 0.52);
        ctx.lineWidth = 2;
        ctx.stroke();
    };

    const drawBeacon = (t, cam) => {
        const bt = beaconPhase(t);
        const at = (tt) => {
            const p = pathPlane[Math.round(clamp01(tt) * PATH_N)];
            return project(p.u, p.v, cam);
        };
        const ef = reduced ? 1 : clamp01(bt / 0.05) * clamp01((1 - bt) / 0.05);
        const SPAN = 0.16;
        const STEPS = 28;
        for (let pass = 0; pass < 2; pass++) {
            for (let k = 0; k < STEPS; k++) {
                const tB = bt - (SPAN * k) / STEPS;
                const tA = bt - (SPAN * (k + 1)) / STEPS;
                if (tB <= 0) continue;
                const a = at(tA);
                const b = at(tB);
                const fade = Math.pow(1 - k / STEPS, 1.5) * ef;
                if (pass === 0) {
                    ctx.strokeStyle = rgba(AMBER, fade * 0.18);
                    ctx.lineWidth = 8;
                } else {
                    ctx.strokeStyle = rgba(AMBER_HOT, fade * 0.98);
                    ctx.lineWidth = 2.2;
                }
                ctx.beginPath();
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
                ctx.stroke();
            }
        }
        const d = at(bt);
        const pulse = reduced ? 0.5 : Math.sin(t * 1.6) * 0.5 + 0.5;
        const halo = ctx.createRadialGradient(d.x, d.y, 0, d.x, d.y, 20 + pulse * 5);
        halo.addColorStop(0, rgba(AMBER_HOT, 0.32 * ef));
        halo.addColorStop(1, rgba(AMBER_HOT, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 20 + pulse * 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = rgba(AMBER, 0.65 * ef);
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 8 + pulse * 1.5, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = rgba(AMBER_HOT, ef);
        ctx.beginPath();
        ctx.arc(d.x, d.y, 3.6, 0, Math.PI * 2);
        ctx.fill();
    };

    const drawAtmosphere = () => {
        if (isPortrait) {
            const top = ctx.createLinearGradient(0, 0, 0, H * 0.58);
            top.addColorStop(0, "rgba(17,21,30,0.78)");
            top.addColorStop(1, "rgba(17,21,30,0)");
            ctx.fillStyle = top;
            ctx.fillRect(0, 0, W, H * 0.58);
        } else {
            const left = ctx.createLinearGradient(0, 0, W * 0.56, 0);
            left.addColorStop(0, "rgba(17,21,30,0.72)");
            left.addColorStop(1, "rgba(17,21,30,0)");
            ctx.fillStyle = left;
            ctx.fillRect(0, 0, W * 0.56, H);
        }
        const bot = ctx.createLinearGradient(0, H - H * 0.16, 0, H);
        bot.addColorStop(0, "rgba(17,21,30,0)");
        bot.addColorStop(1, "rgba(17,21,30,0.5)");
        ctx.fillStyle = bot;
        ctx.fillRect(0, H - H * 0.16, W, H * 0.16);
        const vg = ctx.createRadialGradient(W * 0.5, H * 0.5, H * 0.32, W * 0.5, H * 0.5, H * 0.98);
        vg.addColorStop(0, "rgba(17,21,30,0)");
        vg.addColorStop(1, "rgba(17,21,30,0.5)");
        ctx.fillStyle = vg;
        ctx.fillRect(0, 0, W, H);
        if (grainPattern && !isMobile) {
            ctx.globalAlpha = 0.035;
            ctx.fillStyle = grainPattern;
            ctx.fillRect(0, 0, W, H);
            ctx.globalAlpha = 1;
        }
    };

    const frame = (t) => {
        const cam = isMobile ? 0 : (smx - 0.5) * PARALLAX;
        ctx.clearRect(0, 0, W, H);
        drawGround();
        drawGrid(cam);
        drawPeaks(t, cam);
        drawGuide(cam);
        drawBeacon(t, cam);
        drawAtmosphere();
    };

    const draw = (now) => {
        smx += (mx - smx) * 0.05;
        frame((now - t0) / 1000);
        if (running) raf = requestAnimationFrame(draw);
    };

    const start = () => {
        if (running || reduced) return;
        running = true;
        raf = requestAnimationFrame(draw);
    };
    const stop = () => {
        running = false;
        if (raf) cancelAnimationFrame(raf);
        raf = null;
    };

    const heroEl = cnv.closest("section") || cnv.parentElement;
    if (heroEl) {
        heroEl.addEventListener("pointermove", (e) => {
            const r = heroEl.getBoundingClientRect();
            mx = (e.clientX - r.left) / r.width;
        });
        heroEl.addEventListener("pointerleave", () => {
            mx = 0.5;
        });
    }

    if (reduced) {
        frame(8);
        return;
    }

    if (heroEl && typeof IntersectionObserver !== "undefined") {
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
            { threshold: 0 },
        );
        io.observe(heroEl);
    } else {
        start();
    }

    document.addEventListener("visibilitychange", () => {
        if (document.hidden) stop();
        else if (heroEl && heroEl.getBoundingClientRect().bottom > 0) start();
    });
}
